import { useNostr } from '@nostrify/react';
import { useMutation } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useAppContext } from './useAppContext';
import { useToast } from './useToast';
import type { NostrEvent } from '@nostrify/nostrify';

export interface RebroadcastResult {
  /** Relay URLs that accepted the event */
  succeeded: string[];
  /** Relay URLs that rejected or errored */
  failed: string[];
}

interface RebroadcastInput {
  event: NostrEvent;
  /** When true, suppresses the automatic toast (useful for batching) */
  silent?: boolean;
}

/**
 * Rebroadcast an existing signed event to the user's NIP-65 write relays.
 * Useful for ensuring packs aren't lost if a relay goes offline.
 */
export function useRebroadcast() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { toast } = useToast();

  const writeRelays = config.relayMetadata.relays
    .filter((r) => r.write)
    .map((r) => r.url);

  const writeRelayCount = writeRelays.length;

  const mutation = useMutation({
    mutationFn: async ({ event, silent = false }: RebroadcastInput): Promise<RebroadcastResult & { silent: boolean }> => {
      if (!user) throw new Error('Not logged in');
      if (writeRelays.length === 0) throw new Error('No write relays configured');

      const succeeded: string[] = [];
      const failed: string[] = [];

      // Publish to each write relay individually so we can track per-relay status
      await Promise.allSettled(
        writeRelays.map(async (url) => {
          try {
            const relay = nostr.relay(url);
            await relay.event(event, { signal: AbortSignal.timeout(8000) });
            succeeded.push(url);
          } catch {
            failed.push(url);
          }
        }),
      );

      return { succeeded, failed, silent };
    },
    onSuccess: ({ succeeded, failed, silent }) => {
      if (silent) return;

      if (succeeded.length > 0 && failed.length === 0) {
        toast({
          title: '📡 Rebroadcast complete',
          description: `Published to ${succeeded.length} ${succeeded.length === 1 ? 'relay' : 'relays'} successfully.`,
        });
      } else if (succeeded.length > 0) {
        toast({
          title: '📡 Partially rebroadcast',
          description: `${succeeded.length} succeeded, ${failed.length} failed.`,
        });
      } else {
        toast({
          title: 'Rebroadcast failed',
          description: 'Could not reach any of your write relays.',
          variant: 'destructive',
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: 'Rebroadcast failed',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  return { ...mutation, writeRelayCount };
}
